// ==========================================================================
// SignalFix v2 — Geometry layer
// --------------------------------------------------------------------------
// Draws elements that carry a `geometry` (an array of [lat, lng] points)
// directly on the map instead of as a marker:
//   - segmented elements (New Jersey, marciapiede, guardrail) as styled bands
//   - horizontal signage (crosswalk, stop line, arrow) on the carriageway
// Visibility is zoom-gated per the spec: these only appear once the map is
// close enough to read the carriageway.
// ==========================================================================

import { Polyline, Popup, useMapEvents } from "react-leaflet";
import { useEffect, useState } from "react";
import { getElement, normalizeElementKey } from "../data/signalElements";
import TrafficSignTooltip from "./TrafficSignTooltip";

// Zoom thresholds (align with SIGNAL_ELEMENTS.minZoom for the segmented set).
const SEG_MIN_ZOOM = 16;
const HORIZONTAL_MIN_ZOOM = 17;

// Path styling for horizontal signage drawn on the carriageway.
const HORIZONTAL_STYLES = {
  crosswalk: { color: "#ffffff", weight: 16, opacity: 0.95, dashArray: "3 6", lineCap: "butt" },
  stopline: { color: "#ffffff", weight: 9, opacity: 0.95, lineCap: "butt" },
  arrow: { color: "#ffffff", weight: 6, opacity: 0.95, lineCap: "round" },
};

const hasGeometry = (sign) => Array.isArray(sign?.geometry) && sign.geometry.length >= 2;

const GeometryPopup = ({ sign, onMarkerClick, onDelete, onView3D }) => (
  <Popup autoPan className="ply-3d-popup-override">
    <TrafficSignTooltip
      isInsidePopup
      sign={sign}
      visible
      onView3D={onView3D}
      onEdit={() => onMarkerClick && onMarkerClick(sign)}
      onDelete={(s) => onDelete && onDelete(s)}
      onClose={() => {}}
    />
  </Popup>
);

const SignalFixGeometryLayer = ({ signs = [], onMarkerClick, onDelete, onView3D }) => {
  const [zoom, setZoom] = useState(15);

  const map = useMapEvents({
    zoomend: () => setZoom(map.getZoom()),
  });

  // Sync with the map's current zoom on mount (e.g. after a flyTo).
  useEffect(() => {
    setZoom(map.getZoom());
  }, [map]);

  const geometrySigns = signs.filter(hasGeometry);
  if (geometrySigns.length === 0) return null;

  return (
    <>
      {geometrySigns.map((sign) => {
        const element = getElement(sign.type);
        const isHorizontal = sign.category === "horizontal" || sign.Segnaletica === true;

        // Segmented elements (New Jersey / marciapiede / guardrail).
        if (element?.stroke) {
          if (zoom < (element.minZoom || SEG_MIN_ZOOM)) return null;
          return (
            <Polyline key={sign.id} positions={sign.geometry} pathOptions={element.stroke}>
              <GeometryPopup sign={sign} onMarkerClick={onMarkerClick} onDelete={onDelete} onView3D={onView3D} />
            </Polyline>
          );
        }

        // Horizontal signage drawn on the carriageway at high zoom.
        if (isHorizontal) {
          if (zoom < HORIZONTAL_MIN_ZOOM) return null;
          const kind = sign.geometryKind || guessHorizontalKind(sign.type);
          const style = HORIZONTAL_STYLES[kind] || HORIZONTAL_STYLES.stopline;
          return (
            <Polyline key={sign.id} positions={sign.geometry} pathOptions={style}>
              <GeometryPopup sign={sign} onMarkerClick={onMarkerClick} onDelete={onDelete} onView3D={onView3D} />
            </Polyline>
          );
        }

        return null;
      })}
    </>
  );
};

// Best-effort mapping from a horizontal-signage type to a drawing kind.
function guessHorizontalKind(type) {
  const key = normalizeElementKey(type);
  if (key.includes("attraversamento") || key.includes("pedonale")) return "crosswalk";
  if (key.includes("freccia")) return "arrow";
  return "stopline";
}

export default SignalFixGeometryLayer;
